import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import HttpClient from '../../api/api';
import {Employee, Employees} from './types';
import {removeSessionCache} from '../../utils/session-cache';

const initialState: Employees = {
    items: [],
    offset: 0,
    total: 0,
    canShowAndEditRoles: false,
    canShowAndEditProjects: false,
    canEditAdminLabel: false,
}

const EMPLOYEES_CACHE_KEY = 'cache:employees:all';

const upsertEmployee = (state: Employees, employee: Employee) => {
    const index = state.items.findIndex((item) => item.id === employee.id);

    if (index !== -1) {
        state.items[index] = employee;
        return;
    }

    state.items.push(employee);
    state.total += 1;
}

export const getEmployee = createAsyncThunk(
    'employee/get/one',
    async (id: number) => {
        const response = await HttpClient.employeeApi.getEmployee<Employee>(id);
        return response.data;
    }
)

export const getEmployees = createAsyncThunk(
    'employees/get',
    async (page: number) => {
        const response = await HttpClient.employeeApi.getEmployees<Employees>(page);
        return response.data
    }
)

export const addEmployee = createAsyncThunk(
    'employees/create',
    async (employee: Employee) => {
        const response = await HttpClient.employeeApi.add<Employee>(employee);
        removeSessionCache(EMPLOYEES_CACHE_KEY);
        return response.data
    }
)

export const updateEmployee = createAsyncThunk(
    'employees/update',
    async (employee: Employee) => {
        const response = await HttpClient.employeeApi.update<Employee>(employee);
        removeSessionCache(EMPLOYEES_CACHE_KEY);
        return response.data
    }
)

export const deleteEmployee = createAsyncThunk(
    'employees/delete',
    async (id: number) => {
        const response = await HttpClient.employeeApi.delete<Employee>(id);
        removeSessionCache(EMPLOYEES_CACHE_KEY);
        return response.data
    }
)

const typeSplice = createSlice({
    name: 'employees',
    initialState,
    reducers: {
        clearEmployees: () => {
            return initialState
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getEmployees.fulfilled, (state, action) => {
            if (action.payload.offset === 0) {
                return action.payload
            } else {
                state.items = [...state.items, ...action.payload.items];
                state.offset = action.payload.offset;
            }
        });
        builder.addCase(addEmployee.fulfilled, (state, action) => {
            upsertEmployee(state, action.payload);
        });
        builder.addCase(updateEmployee.fulfilled, (state, action) => {
            upsertEmployee(state, action.payload);
        });
        builder.addCase(deleteEmployee.fulfilled, (state, action) => {
            const nextItems = state.items.filter((item) => item.id !== action.meta.arg);
            if (nextItems.length !== state.items.length) {
                state.items = nextItems;
                state.total = Math.max(0, state.total - 1);
            }
        });
    }
})

export const { clearEmployees } = typeSplice.actions

export default typeSplice.reducer;
